import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import {
  Card,
  CardContent,
  Button,
  Badge,
  Input,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Checkbox,
  Label,
  EmptyState,
  Skeleton,
  ErrorState,
} from '@/components/ui'
import { Webhook, Plus, Edit2, Trash2, AlertCircle, CheckCircle } from 'lucide-react'
import { api, type WebhookInfo } from '@/api/client'

const availableEvents = [
  { id: 'user.created', name: 'user.created', description: 'When a user authorizes the app' },
  { id: 'user.updated', name: 'user.updated', description: 'When user info changes' },
  { id: 'user.deleted', name: 'user.deleted', description: 'When a user disconnects' },
  { id: 'billing.charged', name: 'billing.charged', description: 'When a charge succeeds' },
  { id: 'billing.failed', name: 'billing.failed', description: 'When a charge fails' },
]

export function AppWebhooksPage() {
  const { orgSlug, appId } = useParams()
  const [webhooks, setWebhooks] = useState<WebhookInfo[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [newUrl, setNewUrl] = useState('')
  const [selectedEvents, setSelectedEvents] = useState<string[]>([])
  const [isCreating, setIsCreating] = useState(false)

  useEffect(() => {
    const fetchWebhooks = async () => {
      if (!orgSlug || !appId) return
      try {
        setError(null)
        const response = await api.apps.listWebhooks(orgSlug, appId)
        setWebhooks(response.webhooks)
      } catch (err) {
        console.error('Failed to fetch webhooks:', err)
        setError(err instanceof Error ? err.message : 'Failed to load webhooks')
      } finally {
        setIsLoading(false)
      }
    }

    fetchWebhooks()
  }, [orgSlug, appId])

  const toggleEvent = (eventId: string) => {
    setSelectedEvents((prev) =>
      prev.includes(eventId)
        ? prev.filter((e) => e !== eventId)
        : [...prev, eventId]
    )
  }

  const handleCreate = async () => {
    if (!orgSlug || !appId || !newUrl || selectedEvents.length === 0) return
    setIsCreating(true)
    try {
      const response = await api.apps.createWebhook(orgSlug, appId, {
        url: newUrl,
        events: selectedEvents,
      })
      setWebhooks([...webhooks, response.webhook])
      setCreateDialogOpen(false)
      setNewUrl('')
      setSelectedEvents([])
    } catch (err) {
      console.error('Failed to create webhook:', err)
    } finally {
      setIsCreating(false)
    }
  }

  const handleToggleStatus = async (webhookId: string, currentStatus: string) => {
    if (!orgSlug || !appId) return
    const newStatus = currentStatus === 'active' ? 'paused' : 'active'
    try {
      const response = await api.apps.updateWebhook(orgSlug, appId, webhookId, { status: newStatus })
      setWebhooks(webhooks.map(w =>
        w.id === webhookId ? response.webhook : w
      ))
    } catch (err) {
      console.error('Failed to update webhook:', err)
    }
  }

  const handleDelete = async (webhookId: string) => {
    if (!orgSlug || !appId) return
    try {
      await api.apps.deleteWebhook(orgSlug, appId, webhookId)
      setWebhooks(webhooks.filter(w => w.id !== webhookId))
    } catch (err) {
      console.error('Failed to delete webhook:', err)
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-4 w-64" />
          <Skeleton className="h-9 w-32" />
        </div>
        <div className="space-y-4">
          {[1, 2].map((i) => (
            <Card key={i}>
              <CardContent className="py-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <Skeleton className="h-4 w-64 mb-2" />
                    <div className="flex gap-1 mt-2">
                      <Skeleton className="h-5 w-20" />
                      <Skeleton className="h-5 w-24" />
                    </div>
                    <Skeleton className="h-3 w-40 mt-3" />
                  </div>
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-8 w-8" />
                    <Skeleton className="h-8 w-8" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <ErrorState
        title="Failed to load webhooks"
        description={error}
        action={
          <Button onClick={() => window.location.reload()}>Try Again</Button>
        }
      />
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-[var(--text-secondary)]">
          Receive real-time notifications when events happen in your app
        </p>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Webhook
        </Button>
      </div>

      {/* Webhooks list */}
      {webhooks.length === 0 ? (
        <EmptyState
          icon={Webhook}
          title="No webhooks configured"
          description="Add a webhook to receive event notifications"
          action={
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Webhook
            </Button>
          }
        />
      ) : (
        <div className="space-y-4">
          {webhooks.map((webhook) => (
            <Card key={webhook.id}>
              <CardContent className="py-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <code className="text-sm font-medium text-[var(--text-primary)]">
                        {webhook.url}
                      </code>
                      {webhook.status === 'active' ? (
                        <Badge variant="success">Active</Badge>
                      ) : (
                        <Badge variant="default">{webhook.status}</Badge>
                      )}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {webhook.events.map((event) => (
                        <Badge key={event} variant="default">
                          {event}
                        </Badge>
                      ))}
                    </div>
                    <div className="mt-3 flex items-center gap-2 text-xs text-[var(--text-muted)]">
                      {webhook.last_response_code !== null ? (
                        webhook.last_response_code >= 200 && webhook.last_response_code < 300 ? (
                          <CheckCircle className="h-3 w-3 text-[var(--success)]" />
                        ) : (
                          <AlertCircle className="h-3 w-3 text-[var(--error)]" />
                        )
                      ) : null}
                      <span>
                        {webhook.last_delivery_at
                          ? `Last delivery: ${new Date(webhook.last_delivery_at).toLocaleString()}`
                          : 'No deliveries yet'}
                        {webhook.failure_count > 0 && ` • ${webhook.failure_count} failures`}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleToggleStatus(webhook.id, webhook.status)}
                    >
                      {webhook.status === 'active' ? 'Pause' : 'Enable'}
                    </Button>
                    <Button variant="ghost" size="sm">
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(webhook.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Webhook Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Webhook</DialogTitle>
            <DialogDescription>
              Configure a new webhook endpoint to receive events
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Input
              label="Endpoint URL"
              placeholder="https://api.example.com/webhooks"
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              hint="Must be HTTPS"
            />

            <div className="space-y-2">
              <Label>Events</Label>
              <div className="space-y-2 rounded-lg border border-[var(--border-default)] p-3 max-h-48 overflow-y-auto">
                {availableEvents.map((event) => (
                  <div key={event.id} className="flex items-start gap-2">
                    <Checkbox
                      id={event.id}
                      checked={selectedEvents.includes(event.id)}
                      onCheckedChange={() => toggleEvent(event.id)}
                    />
                    <div>
                      <Label htmlFor={event.id} className="font-normal">
                        {event.name}
                      </Label>
                      <p className="text-xs text-[var(--text-muted)]">
                        {event.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={!newUrl || selectedEvents.length === 0 || isCreating}
              loading={isCreating}
            >
              Add Webhook
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
